import {
    calculateCoordFromPointWithDistance,
    calculateTrigonmetricDistance,
} from "@nova/calculate-geospatial"
import { Coordinate } from "@nova/protobuf"
import { WebMercatorViewport } from "integrations/deckgl"
import { Marker } from "react-map-gl"
import { customMapController, disabledMapController } from "features/map/control/slice"
import { getGlobalMapRef, useViewport } from "features/map/GlobalViewState"
import { popCursorStack, pushCursorStack } from "features/map/MapElements"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveFeatureId } from "panels/content/side-panel/slice.new"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import store from "integrations/redux/store"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"

export const DEFAULT_HALF_WIDTH_KM = 10
export const DEFAULT_HALF_HEIGHT_KM = 7.5
export const MIN_HALF_SIDE_KM = 0.5
export const MAX_HALF_SIDE_KM = 25
export const VIEWPORT_FIT_RATIO = 0.8
export const PIXELATION_ZOOM_THRESHOLD = 12
export const MIN_ZOOM_TO_CONFIRM = 8

export const ZOOM_IN_HINT_TEXT = "Zoom in to set area"

const HANDLE_OUTER_RADIUS = 6
const HANDLE_INNER_RADIUS = 4
const HANDLE_HIT_RADIUS = HANDLE_OUTER_RADIUS + 6
const PIXEL_BLOCK_SIZE = 4
const PIXELATION_REFRESH_MS = 17
const shadowColor = "rgba(0, 0, 0, 0.08)"
const vertexRingColor = "#B1B1B3" // gray-100
const vertexDotColor = "#FFF" // white
const regionBorderColor = "#0C0C0F1A" // black-200
const regionFillImageSrc = "/images/plugins/satellite/sat-mesh-gradient.avif"

type Corner = "nw" | "ne" | "se" | "sw"
type Handle = Corner
type ScreenPoint = [number, number]

export interface RegionSelectorHandle {
    getCorners: () => Coordinate[]
}

function computeCorners(
    centerLng: number,
    centerLat: number,
    halfWidthKm: number,
    halfHeightKm: number
): Coordinate[] {
    const [, northLat] = calculateCoordFromPointWithDistance(
        [centerLng, centerLat],
        0,
        halfHeightKm * 1000
    )
    const [, southLat] = calculateCoordFromPointWithDistance(
        [centerLng, centerLat],
        180,
        halfHeightKm * 1000
    )
    const [eastLng] = calculateCoordFromPointWithDistance(
        [centerLng, centerLat],
        90,
        halfWidthKm * 1000
    )
    const [westLng] = calculateCoordFromPointWithDistance(
        [centerLng, centerLat],
        270,
        halfWidthKm * 1000
    )
    return [
        { longitude: westLng, latitude: northLat },
        { longitude: eastLng, latitude: northLat },
        { longitude: eastLng, latitude: southLat },
        { longitude: westLng, latitude: southLat },
    ]
}

const clampHalfSide = (km: number) => Math.min(MAX_HALF_SIDE_KM, Math.max(MIN_HALF_SIDE_KM, km))

function computeViewportHalfSizeBoundsKm(viewport: WebMercatorViewport) {
    const { width, height } = viewport
    const centerX = width / 2
    const centerY = height / 2
    const [centerLng, centerLat] = viewport.unproject([centerX, centerY])
    const [eastLng, eastLat] = viewport.unproject([
        centerX + (width * VIEWPORT_FIT_RATIO) / 2,
        centerY,
    ])
    const [northLng, northLat] = viewport.unproject([
        centerX,
        centerY - (height * VIEWPORT_FIT_RATIO) / 2,
    ])
    return {
        maxHalfWidthKm:
            calculateTrigonmetricDistance(
                { longitude: centerLng, latitude: centerLat },
                { longitude: eastLng, latitude: eastLat }
            ) / 1000,
        maxHalfHeightKm:
            calculateTrigonmetricDistance(
                { longitude: centerLng, latitude: centerLat },
                { longitude: northLng, latitude: northLat }
            ) / 1000,
    }
}

function getEffectiveHalfSizes(
    viewport: WebMercatorViewport,
    requested: { halfWidthKm: number; halfHeightKm: number }
) {
    const { maxHalfWidthKm, maxHalfHeightKm } = computeViewportHalfSizeBoundsKm(viewport)
    return {
        halfWidthKm: Math.min(requested.halfWidthKm, maxHalfWidthKm),
        halfHeightKm: Math.min(requested.halfHeightKm, maxHalfHeightKm),
    }
}

export const RegionSelector = forwardRef<RegionSelectorHandle>((_props, ref) => {
    const viewport = useViewport()
    const viewportRef = useRef(viewport)
    viewportRef.current = viewport

    const sizeRef = useRef({
        halfWidthKm: DEFAULT_HALF_WIDTH_KM,
        halfHeightKm: DEFAULT_HALF_HEIGHT_KM,
    })

    useImperativeHandle(ref, () => ({
        getCorners: () => {
            const { longitude, latitude } = viewportRef.current
            const { halfWidthKm, halfHeightKm } = getEffectiveHalfSizes(
                viewportRef.current,
                sizeRef.current
            )
            return computeCorners(longitude, latitude, halfWidthKm, halfHeightKm)
        },
    }))

    useEffect(() => {
        const mapEl = document.getElementById("map")
        if (!mapEl) return

        const canvas = document.createElement("canvas")
        canvas.style.cssText =
            "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;"
        mapEl.appendChild(canvas)

        let regionFillImageLoaded = false
        const regionFillImage = new Image()
        regionFillImage.onload = () => {
            regionFillImageLoaded = true
        }
        regionFillImage.src = regionFillImageSrc

        let animationFrameId: number
        let dragHandle: Handle | null = null
        let hoveredHandle: Handle | null = null
        let pixelationSnapshot: HTMLCanvasElement | null = null
        let pixelationSnapshotAt = 0
        let captureUnavailable = false

        let cursorPushed = false
        const updateCursor = () => {
            const shouldShowPointer = !!hoveredHandle || !!dragHandle
            if (shouldShowPointer && !cursorPushed) {
                pushCursorStack("pointer")
                cursorPushed = true
            } else if (!shouldShowPointer && cursorPushed) {
                popCursorStack()
                cursorPushed = false
            }
        }

        const syncCanvasSize = () => {
            if (canvas.width !== mapEl.offsetWidth) canvas.width = mapEl.offsetWidth
            if (canvas.height !== mapEl.offsetHeight) canvas.height = mapEl.offsetHeight
        }

        const project = (c: Coordinate): ScreenPoint =>
            viewportRef.current.project([c.longitude, c.latitude]) as ScreenPoint

        const getHandlePositions = (): Record<Handle, ScreenPoint> => {
            const { longitude, latitude } = viewportRef.current
            const { halfWidthKm, halfHeightKm } = getEffectiveHalfSizes(
                viewportRef.current,
                sizeRef.current
            )
            const [nw, ne, se, sw] = computeCorners(
                longitude,
                latitude,
                halfWidthKm,
                halfHeightKm
            ).map(project)
            return { nw, ne, se, sw }
        }

        const updatePixelationSnapshot = (nw: ScreenPoint, se: ScreenPoint) => {
            const now = performance.now()
            if (pixelationSnapshot && now - pixelationSnapshotAt < PIXELATION_REFRESH_MS) return
            pixelationSnapshotAt = now

            const mapCanvas = getGlobalMapRef()?.current?.getMap()?.getCanvas()
            const zoom = getGlobalMapRef()?.current?.getZoom()
            if (!mapCanvas || !zoom) return

            const x = Math.min(nw[0], se[0])
            const y = Math.min(nw[1], se[1])
            const w = Math.abs(se[0] - nw[0])
            const h = Math.abs(se[1] - nw[1])
            if (w < 1 || h < 1) return

            const scaleX = mapCanvas.width / mapCanvas.clientWidth
            const scaleY = mapCanvas.height / mapCanvas.clientHeight

            const pixelBlockSize = Math.min(Math.max(2 * (zoom - PIXELATION_ZOOM_THRESHOLD), 4), 32)
            const cols = Math.max(1, Math.round(w / pixelBlockSize))
            const rows = Math.max(1, Math.round(h / pixelBlockSize))

            const tiny = document.createElement("canvas")
            tiny.width = cols
            tiny.height = rows
            const tinyCtx = tiny.getContext("2d")
            if (!tinyCtx) return

            tinyCtx.drawImage(
                mapCanvas,
                x * scaleX,
                y * scaleY,
                w * scaleX,
                h * scaleY,
                0,
                0,
                cols,
                rows
            )
            tinyCtx.getImageData(0, 0, 1, 1)
            pixelationSnapshot = tiny
        }

        const drawFallbackMosaic = (
            ctx: CanvasRenderingContext2D,
            nw: ScreenPoint,
            se: ScreenPoint
        ) => {
            const x = Math.min(nw[0], se[0])
            const y = Math.min(nw[1], se[1])
            const w = Math.abs(se[0] - nw[0])
            const h = Math.abs(se[1] - nw[1])
            ctx.save()
            ctx.fillStyle = "rgba(255, 255, 255, 0.15)"
            for (let by = 0; by < h; by += PIXEL_BLOCK_SIZE) {
                for (let bx = 0; bx < w; bx += PIXEL_BLOCK_SIZE) {
                    if (
                        (Math.round(bx / PIXEL_BLOCK_SIZE) + Math.round(by / PIXEL_BLOCK_SIZE)) %
                            2 ===
                        0
                    ) {
                        ctx.fillRect(x + bx, y + by, PIXEL_BLOCK_SIZE, PIXEL_BLOCK_SIZE)
                    }
                }
            }
            ctx.restore()
        }

        const drawPixelation = (
            ctx: CanvasRenderingContext2D,
            nw: ScreenPoint,
            se: ScreenPoint
        ) => {
            if (!captureUnavailable) {
                try {
                    updatePixelationSnapshot(nw, se)
                } catch {
                    captureUnavailable = true
                    pixelationSnapshot = null
                }
            }

            if (captureUnavailable || !pixelationSnapshot) {
                drawFallbackMosaic(ctx, nw, se)
                return
            }

            const x = Math.min(nw[0], se[0])
            const y = Math.min(nw[1], se[1])
            const w = Math.abs(se[0] - nw[0])
            const h = Math.abs(se[1] - nw[1])
            ctx.save()
            ctx.imageSmoothingEnabled = false
            ctx.drawImage(
                pixelationSnapshot,
                0,
                0,
                pixelationSnapshot.width,
                pixelationSnapshot.height,
                x,
                y,
                w,
                h
            )
            ctx.restore()
        }

        const render = () => {
            syncCanvasSize()
            const ctx = canvas.getContext("2d")
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.shadowColor = "transparent"
                ctx.shadowBlur = 0
                ctx.shadowOffsetX = 0
                ctx.shadowOffsetY = 0

                if (viewportRef.current.zoom >= MIN_ZOOM_TO_CONFIRM) {
                    const handles = getHandlePositions()

                    // Shrink the shadow-casting fill 1px inward to avoid residual ring
                    const cx = (handles.nw[0] + handles.ne[0] + handles.se[0] + handles.sw[0]) / 4
                    const cy = (handles.nw[1] + handles.ne[1] + handles.se[1] + handles.sw[1]) / 4
                    const insetToward1px = (p: ScreenPoint): ScreenPoint => {
                        const dx = cx - p[0]
                        const dy = cy - p[1]
                        const len = Math.hypot(dx, dy) || 1
                        return [p[0] + (dx / len) * 1, p[1] + (dy / len) * 1]
                    }
                    const [nwIn, neIn, seIn, swIn] = [
                        handles.nw,
                        handles.ne,
                        handles.se,
                        handles.sw,
                    ].map(insetToward1px)

                    ctx.save()
                    ctx.beginPath()
                    ctx.moveTo(...nwIn)
                    ctx.lineTo(...neIn)
                    ctx.lineTo(...seIn)
                    ctx.lineTo(...swIn)
                    ctx.closePath()
                    ctx.shadowColor = shadowColor
                    ctx.shadowBlur = 4
                    ctx.shadowOffsetX = 0
                    ctx.shadowOffsetY = 4
                    ctx.fillStyle = "#000"
                    ctx.fill()
                    ctx.restore()

                    ctx.beginPath()
                    ctx.moveTo(...handles.nw)
                    ctx.lineTo(...handles.ne)
                    ctx.lineTo(...handles.se)
                    ctx.lineTo(...handles.sw)
                    ctx.closePath()

                    ctx.save()
                    ctx.globalCompositeOperation = "destination-out"
                    ctx.fillStyle = "#000"
                    ctx.fill()
                    ctx.restore()

                    ctx.save()
                    ctx.clip()
                    // Drawn after the destination-out punch and inside the clip, so it isn't wiped out by the punch.
                    if (viewportRef.current.zoom > PIXELATION_ZOOM_THRESHOLD) {
                        drawPixelation(ctx, handles.nw, handles.se)
                    }
                    if (regionFillImageLoaded) {
                        const x = Math.min(handles.nw[0], handles.se[0])
                        const y = Math.min(handles.nw[1], handles.se[1])
                        const w = Math.abs(handles.se[0] - handles.nw[0])
                        const h = Math.abs(handles.se[1] - handles.nw[1])
                        ctx.globalAlpha = 0.3
                        ctx.drawImage(regionFillImage, x, y, w, h)
                        ctx.globalAlpha = 1
                    }
                    ctx.strokeStyle = regionBorderColor
                    ctx.stroke()
                    ctx.restore()

                    for (const [handle, point] of Object.entries(handles) as [
                        Handle,
                        ScreenPoint,
                    ][]) {
                        const isActive = handle === dragHandle || handle === hoveredHandle

                        ctx.shadowColor = isActive ? "transparent" : shadowColor
                        ctx.shadowBlur = isActive ? 0 : 4
                        ctx.shadowOffsetX = 0
                        ctx.shadowOffsetY = isActive ? 0 : 4

                        ctx.beginPath()
                        ctx.arc(point[0], point[1], HANDLE_OUTER_RADIUS, 0, Math.PI * 2)
                        ctx.fillStyle = vertexRingColor
                        ctx.fill()

                        ctx.beginPath()
                        ctx.arc(point[0], point[1], HANDLE_INNER_RADIUS, 0, Math.PI * 2)
                        ctx.fillStyle = vertexDotColor
                        ctx.fill()
                    }
                }
            }
            animationFrameId = requestAnimationFrame(render)
        }
        animationFrameId = requestAnimationFrame(render)

        const hitTestHandle = (x: number, y: number): Handle | null => {
            const handles = getHandlePositions()
            for (const [handle, point] of Object.entries(handles) as [Handle, ScreenPoint][]) {
                if (Math.hypot(point[0] - x, point[1] - y) <= HANDLE_HIT_RADIUS) return handle
            }
            return null
        }

        const handleMouseDown = (e: MouseEvent) => {
            if (viewportRef.current.zoom < MIN_ZOOM_TO_CONFIRM) return
            if ((e.target as HTMLElement)?.className !== "maplibregl-canvas") return
            const rect = mapEl.getBoundingClientRect()
            const handle = hitTestHandle(e.clientX - rect.left, e.clientY - rect.top)
            if (!handle) return
            e.stopPropagation()
            dragHandle = handle
            updateCursor()
            store.dispatch(disabledMapController())
        }

        const handleMouseMove = (e: MouseEvent) => {
            const rect = mapEl.getBoundingClientRect()
            const x = e.clientX - rect.left
            const y = e.clientY - rect.top

            hoveredHandle = hitTestHandle(x, y)
            updateCursor()

            if (!dragHandle) return

            const [lng, lat] = viewportRef.current.unproject([x, y])
            const { longitude, latitude } = viewportRef.current
            const widthKm =
                calculateTrigonmetricDistance(
                    { latitude, longitude },
                    { latitude, longitude: lng }
                ) / 1000
            const heightKm =
                calculateTrigonmetricDistance(
                    { latitude, longitude },
                    { latitude: lat, longitude }
                ) / 1000

            sizeRef.current = {
                halfWidthKm: clampHalfSide(widthKm),
                halfHeightKm: clampHalfSide(heightKm),
            }
        }

        const handleMouseUp = () => {
            if (!dragHandle) return
            dragHandle = null
            updateCursor()
            store.dispatch(
                customMapController({ options: { dragRotate: false, doubleClickZoom: false } })
            )
        }

        mapEl.addEventListener("mousedown", handleMouseDown, { capture: true })
        window.addEventListener("mousemove", handleMouseMove)
        window.addEventListener("mouseup", handleMouseUp)

        return () => {
            // Unmounting mid-drag (e.g. Escape, switching plugins) would otherwise leave the map controller disabled forever.
            handleMouseUp()
            cancelAnimationFrame(animationFrameId)
            canvas.remove()
            if (cursorPushed) popCursorStack()
            mapEl.removeEventListener("mousedown", handleMouseDown, { capture: true })
            window.removeEventListener("mousemove", handleMouseMove)
            window.removeEventListener("mouseup", handleMouseUp)
        }
    }, [])

    return null
})
RegionSelector.displayName = "RegionSelector"

export const RegionSelectorZoomHint = () => {
    const isActive = useAppSelector(selectActiveFeatureId) === NovaPluginIds.Satellite
    const { zoom } = useViewport()
    const [coords, setCoords] = useState<{ longitude: number; latitude: number }>()

    useEffect(() => {
        if (!isActive) {
            setCoords(undefined)
            return
        }
        const listener = (event: MouseEvent) => {
            if ((event.target as HTMLElement)?.className === "maplibregl-canvas") {
                setCoords(resolveEventLngLat(event))
            } else {
                setCoords(undefined)
            }
        }
        window.addEventListener("mousemove", listener)
        return () => window.removeEventListener("mousemove", listener)
    }, [isActive])

    const isTooFarOut = zoom < MIN_ZOOM_TO_CONFIRM
    const isTooFarIn = zoom > PIXELATION_ZOOM_THRESHOLD

    if (!isActive || !coords || (!isTooFarOut && !isTooFarIn)) return null

    return (
        <Marker
            longitude={coords.longitude}
            latitude={coords.latitude}
            anchor="top-left"
            offset={[12, 16]}
            style={{ pointerEvents: "none", zIndex: 20 }}
        >
            <div className="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none">
                {isTooFarOut ? (
                    <small className="text-gray-50">{ZOOM_IN_HINT_TEXT}</small>
                ) : (
                    <div className="flex flex-col">
                        <small className="text-gray-50">Zoom out for better resolution.</small>
                        <small className="text-gray-200">
                            Mosaic will be pixelated at this zoom level.
                        </small>
                    </div>
                )}
            </div>
        </Marker>
    )
}
